// src/communaute/Publications.jsx
//
// Deux onglets de la page Communauté :
//   <FilCommunaute />    — toutes les publications (scroll infini)
//   <MesPublications />  — mes annonces + bouton « Nouvelle annonce »

import { useCallback, useEffect, useState } from 'react';
import { FileText, Plus, RefreshCw } from 'lucide-react';

import AddPostModal from './AddPostModal';
import { getMesPosts, getPosts } from './api';
import PostCard from './PostCard';
import { Chargement, Spinner, Vide, btnPrimaire, btnSecondaire, useScrollInfini } from './ui';

// ══════════════════════════════════════════════════════
//  LISTE PAGINÉE GÉNÉRIQUE
// ══════════════════════════════════════════════════════
function usePostsPagines(charger, deps) {
  const [posts, setPosts] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [chargementPlus, setChargementPlus] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const recharger = useCallback(async () => {
    setChargement(true);
    const r = await charger(1);
    setPosts(r.items);
    setPage(r.page);
    setLastPage(r.lastPage);
    setChargement(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    recharger();
  }, [recharger]);

  const suite = useCallback(async () => {
    if (chargementPlus || page >= lastPage) return;
    setChargementPlus(true);
    const r = await charger(page + 1);
    setPosts((l) => {
      const ids = new Set(l.map((p) => p.id));
      return [...l, ...r.items.filter((p) => !ids.has(p.id))];
    });
    setPage(r.page);
    setLastPage(r.lastPage);
    setChargementPlus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargementPlus, page, lastPage, ...deps]);

  const sentinelle = useScrollInfini(suite, !chargement && page < lastPage);

  return { posts, setPosts, chargement, chargementPlus, recharger, sentinelle };
}

function Liste({ posts, setPosts, chargementPlus, sentinelle }) {
  return (
    <div className="space-y-4">
      {posts.map((p) => (
        <PostCard key={p.id} post={p} onSupprime={(id) => setPosts((l) => l.filter((x) => x.id !== id))} />
      ))}
      <div ref={sentinelle} />
      {chargementPlus && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  FIL COMMUNAUTÉ
// ══════════════════════════════════════════════════════
export function FilCommunaute() {
  const liste = usePostsPagines((p) => getPosts(p), []);

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-5 flex justify-end">
        <button onClick={liste.recharger} className={btnSecondaire} title="Actualiser">
          <RefreshCw size={16} /> Actualiser
        </button>
      </div>

      {liste.chargement ? (
        <Chargement />
      ) : liste.posts.length === 0 ? (
        <Vide icone={FileText} titre="Aucune publication pour le moment" />
      ) : (
        <Liste {...liste} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  MES PUBLICATIONS
// ══════════════════════════════════════════════════════
export function MesPublications() {
  const [ajout, setAjout] = useState(false);
  const liste = usePostsPagines((p) => getMesPosts(p), []);

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">Vos annonces publiées dans la communauté</p>
        <button onClick={() => setAjout(true)} className={btnPrimaire}>
          <Plus size={18} /> Nouvelle annonce
        </button>
      </div>

      {liste.chargement ? (
        <Chargement />
      ) : liste.posts.length === 0 ? (
        <Vide
          icone={FileText}
          titre="Vous n'avez publié aucune annonce"
          sous="Cliquez sur « Nouvelle annonce » pour créer votre première publication"
        />
      ) : (
        <Liste {...liste} />
      )}

      <AddPostModal
        ouvert={ajout}
        onFermer={() => setAjout(false)}
        onCree={(p) => liste.setPosts((l) => [p, ...l])}
      />
    </div>
  );
}